import { Measures } from '@lidea/shared/util/types';
import { hashKey, type QueryClient } from '@tanstack/react-query';
import { produce } from 'immer';

const UPDATE_MEASURE_MUTATION_KEY = ['updateMeasure'] as const;

type UpdateMeasureVariables = {
  id: number;
  data: { [key: string]: number | string | null };
};

/**
 * Patch field values from a single updateMeasure payload onto a Measures draft.
 */
export function patchMeasureFields(
  draft: Measures,
  measureId: number,
  data: UpdateMeasureVariables['data']
): void {
  const measure = draft[measureId];
  if (!measure) {
    return;
  }

  for (const [inputKey, inputValue] of Object.entries(data)) {
    measure.dynamic.forEach((dynamic) => {
      for (const field of dynamic.fields) {
        if (field.name === inputKey) {
          field.value = inputValue;
          break;
        }
      }
    });

    if (measure.static) {
      for (const field of measure.static) {
        if (field.name === inputKey) {
          field.value = inputValue;
          break;
        }
      }
    }
  }
}

/**
 * Re-apply paused/pending updateMeasure mutations onto fresh server measures
 * so HL offline edits survive refetches that still hit the network.
 */
export function applyPendingMeasureUpdates(
  measures: Measures,
  queryClient: QueryClient
): Measures {
  const pending = queryClient.getMutationCache().findAll({
    mutationKey: [...UPDATE_MEASURE_MUTATION_KEY],
    status: 'pending',
  });

  if (pending.length === 0) {
    return measures;
  }

  const keyHash = hashKey(UPDATE_MEASURE_MUTATION_KEY);

  return produce(measures, (draft) => {
    for (const mutation of pending) {
      if (
        !mutation.options.mutationKey ||
        hashKey(mutation.options.mutationKey) !== keyHash
      ) {
        continue;
      }
      const variables = mutation.state.variables as
        | UpdateMeasureVariables
        | undefined;
      if (!variables?.id || !variables.data) {
        continue;
      }
      patchMeasureFields(draft, variables.id, variables.data);
    }
  });
}
